import React from 'react';
import { Typography, Icon } from '@scale/scaleui';
import { formatDistanceToNow } from 'date-fns';
import type INotificationSend from '@scale/shared/types/notifications/notificationSend';
import { useDismissNotification } from './_data/useOpsHubNotifications';
import {
  NOTIFICATION_SEVERITY_ICONS,
  NOTIFICATION_SEVERITY_COLORS,
  NOTIFICATION_SEVERITY_BADGE_COLORS,
} from './constants';
import { useHistory } from 'react-router-dom';
import { GenAIOpsHubPaths } from 'react-scale/corp/views/CorpProjectGroupManager/constants';
import { logNotificationClick } from './_data/telemetry';
import { useUser } from 'react-scale/components/ui/UserContext';
import { Badge } from '@scale/scaleui-radix';

interface NotificationMessageProps {
  notificationSend: INotificationSend;
}

const NotificationMessage: React.FC<NotificationMessageProps> = ({ notificationSend }) => {
  const history = useHistory();
  const user = useUser();
  const isUnread = !notificationSend.dismissedAt;
  const notificationContent =
    typeof notificationSend.notification === 'object' ? notificationSend.notification : null;
  const { mutate: dismissNotification } = useDismissNotification();

  if (!notificationContent) return null;

  const handleClick = async (event: React.MouseEvent<HTMLLIElement>) => {
    event.stopPropagation();
    if (notificationSend._id) {
      logNotificationClick(
        notificationSend._id,
        notificationContent.severity,
        notificationContent.title,
        'dropdown',
        user,
      );
      dismissNotification(notificationSend._id);
      history.push(`${GenAIOpsHubPaths.NotificationCenter}?notificationId=${notificationSend._id}`);
    }
  };

  return (
    <li
      onClick={handleClick}
      className={`border-b border-neutral-200 flex items-center px-1.5 cursor-pointer h-[76px] w-full overflow-hidden ${
        isUnread ? 'bg-[rgba(0,96,255,0.03)] hover:bg-[rgba(0,96,255,0.05)]' : 'hover:bg-neutral-50'
      }`}
    >
      <div className="flex items-center gap-1 w-full">
        <Icon
          icon={NOTIFICATION_SEVERITY_ICONS[notificationContent.severity]}
          iconClassName={NOTIFICATION_SEVERITY_COLORS[notificationContent.severity]}
          className={`flex-shrink-0`}
          size="lg"
        />
        <div className="flex-1 min-w-0 overflow-hidden">
          <div className="flex flex-col overflow-hidden">
            <div className="flex items-center gap-2">
              <Badge
                size="1"
                color={NOTIFICATION_SEVERITY_BADGE_COLORS[notificationContent.severity]}
              >
                {notificationContent.severity
                  .replace(/_/g, ' ')
                  .split(' ')
                  .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                  .join(' ')}
              </Badge>
              {notificationSend.projectName && (
                <Typography.Paragraph2 className="text-sm text-neutral-600 truncate">
                  {notificationSend.projectName}
                </Typography.Paragraph2>
              )}
            </div>
            <Typography.Subtitle4
              className={`text-sm truncate ${isUnread ? 'text-neutral-900' : 'text-neutral-900'}`}
            >
              {notificationContent.title}
            </Typography.Subtitle4>
            <p className="text-xs text-neutral-500">
              {formatDistanceToNow(new Date(notificationSend.createdAt), { addSuffix: true })}
            </p>
          </div>
        </div>
        <div
          className={`w-2 h-2 rounded-full flex-shrink-0 mx-1 ${
            isUnread ? 'bg-[#6544E5]' : 'bg-transparent'
          }`}
        />
      </div>
    </li>
  );
};

export default NotificationMessage;
